// src/collection/CollectionApp.tsx
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import axios from "axios";
import { MAPBOX_ACCESS_TOKEN, MAPBOX_STYLE } from "src/config";
import { upsertRoute, upsertRouteNodes } from "./map/routeLayers";
import {
  createStationMarkers,
  clearStationMarkers,
} from "./map/stationMarkers";
import { buildSyntheticRoute } from "./map/syntheticRoute";
import StationDock from "./ui/StationDock";
import IframeModal from "./ui/IframeModal";
import { StationInfo } from "./types";

type CollectionAppProps = { id: string | null };
type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: any | null };

type FC = GeoJSON.FeatureCollection;

const COLLECTIONS_API = "https://api.rdx.social/v2";

const SCL_CENTER: [number, number] = [-70.6506, -33.4372]; // fallback

export default function CollectionApp({ id }: CollectionAppProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [sceneReady, setSceneReady] = useState(false);
  const [selected, setSelected] = useState<StationInfo | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  // Ruta REAL para encuadre final
  const routeRealRef = useRef<FC | null>(null);

  // Cruise y órbita
  const cruiseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cruiseRunningRef = useRef(false);
  const orbitRafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const orbitActiveRef = useRef<boolean>(false);
  const userInteractedRef = useRef(false);

  // ============= LOAD → CREATE MAP (in anchor) → RENDER =============
  useEffect(() => {
    let cancel = false;

    async function boot() {
      setSceneReady(false);
      userInteractedRef.current = false;
      routeRealRef.current = null;

      // Limpia mapa anterior si cambia el id
      if (mapRef.current) {
        stopOrbit();
        stopCruise();
        clearStationMarkers(mapRef.current);
        mapRef.current.remove();
        mapRef.current = null;
      }

      if (!id) {
        setState({ kind: "ready", data: null });
        // Sin datos, crea mapa mínimo en SCL solo para tener algo si lo necesitas
        const map = createBaseMap(SCL_CENTER);
        if (map) setSceneReady(true);
        return;
      }

      setState({ kind: "loading" });

      try {
        const { data } = await axios.get(
          `${COLLECTIONS_API}/collections/${id}`
        );
        if (cancel) return;

        const routeFC = asFC(data?.structure);
        const stationsFC = asFC(data?.stations);

        setState({ kind: "ready", data });
        routeRealRef.current = routeFC ?? null;

        // === 1) Calcula ancla ANTES de crear el mapa (evita 0,0 y flashes) ===
        const anchor = pickInitialCoord(routeFC, stationsFC);

        // === 2) Crea el mapa centrado EN el ancla ===
        const map = createBaseMap(anchor);
        if (!map) return;

        // Bloqueamos interacción durante intro
        disableMapInteractions(map);

        // === 3) Pinto RUTA REAL (visible) y puntos de ruta ===
        if (routeFC) {
          upsertRoute(map, "route-structure-src", routeFC);
          upsertRouteNodes(
            map,
            "route-structure-src",
            "route-points-src",
            "route-points-lyr",
            routeFC
          );

          // Esperamos fuentes/capas
          await waitForSources(map, [
            "route-structure-src",
            "route-points-src",
          ]);

          // === 4) Construyo ruta suavizada SOLO para la cámara (no visible) ===
          const smoothCamFC = buildSyntheticRoute(routeFC, {
            stepMeters: 6000,
            maxMeters: 90000,
            segmentsPerSpan: 14,
            tension: 0.45,
          });

          const camLine = collectLineCoords(smoothCamFC);
          const cruiseStart = camLine[0] ?? anchor;

          // MUY IMPORTANTE: situamos cámara EXACTAMENTE en el punto de arranque del cruise
          map.jumpTo({
            center: cruiseStart as any,
            zoom: 11.5,
            pitch: 70,
            bearing: 120,
          });

          // Espera a que el mapa renderice ESTE frame con todo pintado, luego quitamos loader y arrancamos cruise
          await waitNextRender(map);
          setSceneReady(true);

          // Pequeño respiro y arranca cruise
          setTimeout(() => startCruiseAlongRoute(smoothCamFC), 200);
        } else {
          // No hay ruta: muestra escena y habilita interacción
          await waitNextRender(map);
          setSceneReady(true);
          enableMapInteractions(map);
        }

        // Estaciones
        if (stationsFC) {
          createStationMarkers(map, stationsFC, (st) => {
            setSelected(st);
            if (st.lng != null && st.lat != null) flyToStation(st.lng, st.lat);
            stopCruise();
            stopOrbit();
            enableMapInteractions(map);
          });
        }

        // Cancelar intro/orbit si hay interacción
        const cancelOnInteraction = () => {
          if (userInteractedRef.current) return;
          userInteractedRef.current = true;
          stopCruise();
          stopOrbit();
          enableMapInteractions(map);
          map.off("mousedown", cancelOnInteraction);
          map.off("wheel", cancelOnInteraction);
          map.off("touchstart", cancelOnInteraction);
        };
        map.on("mousedown", cancelOnInteraction);
        map.on("wheel", cancelOnInteraction);
        map.on("touchstart", cancelOnInteraction);
      } catch (err: any) {
        if (cancel) return;

        setState({
          kind: "error",
          message:
            err?.response?.status === 404
              ? "No se encontró información para esta colección."
              : err?.message ?? "Error al cargar la colección.",
        });

        // En error igual creamos mapa (fallback SCL) y orbitamos terreno
        const map = createBaseMap(SCL_CENTER);
        if (map) {
          disableMapInteractions(map);
          setSceneReady(true);
          startOrbit(); // demo terreno
        }
      }
    }

    boot();
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ============= UI =============
  const statusOverlay = (() => {
    if (state.kind === "error") {
      return (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/85">
          <div className="text-center text-white">
            <h2 className="text-2xl font-light">
              No se ha encontrado información
            </h2>
            <p className="mt-2 opacity-80">{state.message}</p>
            <p className="mt-4 text-sm opacity-60">
              Vista del terreno — rotación automática
            </p>
          </div>
        </div>
      );
    }
    if (state.kind === "ready" && !id) {
      return (
        <div className="pointer-events-none absolute left-4 top-4 rounded-lg bg-black/60 px-3 py-2 text-white">
          Ingresa con un enlace válido para una colección.
        </div>
      );
    }
    return null;
  })();

  const showBlackLoader = state.kind === "loading" || !sceneReady;

  return (
    <div className="relative min-h-screen w-full">
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        .fade-in { animation: fadeIn .32s ease-out both; }
      `}</style>

      <div ref={containerRef} className="h-screen w-full" />

      {/* LOADER NEGRO */}
      {showBlackLoader && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black text-white">
          <span className="text-sm opacity-70">Preparando escena…</span>
        </div>
      )}

      {/* Dock derecho */}
      {selected && (
        <StationDock
          station={selected}
          onClose={() => setSelected(null)}
          onOpenStation={(id) =>
            setIframeUrl(`https://app.rdx.social/station/${id}`)
          }
        />
      )}

      {/* Modal 360 en 90vw x 90vh */}
      {iframeUrl && (
        <IframeModal url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}

      {statusOverlay}
    </div>
  );

  // ======== helpers ========
  function createBaseMap(center: [number, number]): mapboxgl.Map | null {
    if (!containerRef.current) return null;
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAPBOX_STYLE,
      center, // ← ancla calculada (o cruiseStart)
      zoom: 11.5,
      bearing: 120,
      pitch: 70,
      projection: { name: "globe" },
      antialias: true,
      renderWorldCopies: false,
      fadeDuration: 0, // minimiza cross-fade y flashes al cargar
    });

    // Sin controles UI
    // map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), "top-right");

    map.on("load", () => {
      try {
        if (!map.getSource("mapbox-dem")) {
          map.addSource("mapbox-dem", {
            type: "raster-dem",
            url: "mapbox://mapbox.mapbox-terrain-dem-v1",
            tileSize: 512,
            maxzoom: 14,
          } as any);
        }
        map.setTerrain({ source: "mapbox-dem", exaggeration: 1.6 });
        map.setFog({
          range: [0.5, 10],
          color: "white",
          "horizon-blend": 0.2,
          "high-color": "#add8e6",
          "space-color": "#000000",
          "star-intensity": 0.15,
        } as any);
      } catch {}
    });

    mapRef.current = map;
    return map;
  }

  function flyToStation(lng: number, lat: number) {
    const map = mapRef.current;
    if (!map) return;
    userInteractedRef.current = true;
    stopCruise();
    stopOrbit();
    enableMapInteractions(map);
    map.flyTo({
      center: [lng, lat],
      zoom: Math.min(16, Math.max(map.getZoom(), 14.2)),
      pitch: 75,
      speed: 0.6,
      curve: 1.5,
      essential: true,
    });
  }

  /** ========== Cruise con ruta suavizada (no visible) ========== */
  function startCruiseAlongRoute(smoothFC: FC) {
    const map = mapRef.current;
    if (!map) return;
    stopCruise();

    const coords = collectLineCoords(smoothFC);
    if (coords.length < 2) return;

    const CRUISE_STEP_METERS = 7000;
    const sampled = sampleLineByStep(coords, CRUISE_STEP_METERS);

    // Asegura que la cámara YA está en el primer punto (lo hicimos antes de quitar el loader)
    cruiseRunningRef.current = true;
    let i = 1;

    const step = () => {
      if (!cruiseRunningRef.current || !map) return;

      if (i >= sampled.length) {
        const routeReal = routeRealRef.current;
        if (routeReal) {
          finishCruiseToFullRoute(map, routeReal)
            .then(() => {
              enableMapInteractions(map);
              if (!userInteractedRef.current) {
                setTimeout(() => startOrbit({ subtle: true }), 700);
              }
            })
            .finally(() => stopCruise());
        } else {
          enableMapInteractions(map);
          stopCruise();
        }
        return;
      }

      const curr = sampled[i - 1];
      const next = sampled[i];
      const bearing = bearingTo(curr, next);
      const duration = 2000;

      map.easeTo({
        center: next as any,
        bearing: normalizeBearing(bearing),
        pitch: 72,
        zoom: 12.8,
        duration,
        easing: (t) => t,
        essential: true,
      });

      i += 1;
      cruiseTimerRef.current = setTimeout(step, duration - 120);
    };

    cruiseTimerRef.current = setTimeout(step, 320);
  }

  function finishCruiseToFullRoute(map: mapboxgl.Map, fc: FC): Promise<void> {
    return new Promise((resolve) => {
      const bounds = computeBounds(fc);
      if (!bounds) return resolve();

      const onEnd = () => {
        map.off("moveend", onEnd);
        resolve();
      };
      map.on("moveend", onEnd);

      map.fitBounds(bounds, {
        padding: 90,
        maxZoom: 11.8,
        pitch: 65,
        bearing: map.getBearing(),
        duration: 1400,
        easing: (t) => t,
        essential: true,
      });
    });
  }

  function stopCruise() {
    cruiseRunningRef.current = false;
    if (cruiseTimerRef.current) clearTimeout(cruiseTimerRef.current);
    cruiseTimerRef.current = null;
  }

  /** ========== Orbit (idle) — sin oscilación, solo giro suave de bearing ========== */
  function startOrbit(opts?: { subtle?: boolean }) {
    if (orbitActiveRef.current) return;
    orbitActiveRef.current = true;
    lastTsRef.current = null;

    const angularSpeed = opts?.subtle ? 0.6 : 6; // grados/seg
    const fixedPitch = 65;

    const step = (ts: number) => {
      if (!orbitActiveRef.current) return;
      const map = mapRef.current;
      if (!map) return;
      const last = lastTsRef.current ?? ts;
      const dt = (ts - last) / 1000;
      lastTsRef.current = ts;

      map.setBearing(map.getBearing() + angularSpeed * dt);
      map.setPitch(fixedPitch);

      orbitRafRef.current = requestAnimationFrame(step);
    };
    orbitRafRef.current = requestAnimationFrame(step);
  }
  function stopOrbit() {
    orbitActiveRef.current = false;
    if (orbitRafRef.current !== null) cancelAnimationFrame(orbitRafRef.current);
    orbitRafRef.current = null;
    lastTsRef.current = null;
  }
}

/* ===== Utils ===== */
function asFC(x: any): FC | null {
  if (!x || typeof x !== "object") return null;
  if (x.type === "FeatureCollection" && Array.isArray(x.features))
    return x as FC;
  return null;
}

function computeBounds(fc: FC): mapboxgl.LngLatBoundsLike | null {
  const coords: [number, number][] = [];
  for (const f of fc.features) collectCoords((f as any).geometry, coords);
  if (!coords.length) return null;
  const bounds = coords.reduce(
    (b, c) => b.extend(c as mapboxgl.LngLatLike),
    new mapboxgl.LngLatBounds(coords[0], coords[0])
  );
  return bounds;
}
function collectCoords(
  geom: GeoJSON.Geometry | null | undefined,
  out: [number, number][]
) {
  if (!geom) return;
  switch (geom.type) {
    case "Point":
      out.push(geom.coordinates as [number, number]);
      break;
    case "MultiPoint":
    case "LineString":
      (geom.coordinates as [number, number][]).forEach((c) => out.push(c));
      break;
    case "MultiLineString":
    case "Polygon":
      (geom.coordinates as [number, number][][]).forEach((ring) =>
        ring.forEach((c) => out.push(c))
      );
      break;
    case "MultiPolygon":
      (geom.coordinates as [number, number][][][]).forEach((poly) =>
        poly.forEach((ring) => ring.forEach((c) => out.push(c)))
      );
      break;
    case "GeometryCollection":
      geom.geometries.forEach((g) => collectCoords(g, out));
      break;
  }
}

/** Elección del ancla inicial (ruta → estación → Santiago) */
function firstLineCoord(
  fc: GeoJSON.FeatureCollection | null
): [number, number] | null {
  if (!fc) return null;
  for (const f of fc.features) {
    const g = (f as any).geometry as GeoJSON.Geometry | undefined;
    if (!g) continue;
    if (g.type === "LineString") {
      const arr = g.coordinates as [number, number][];
      if (arr && arr.length) return arr[0];
    } else if (g.type === "MultiLineString") {
      const arr = g.coordinates as [number, number][][];
      if (arr && arr.length && arr[0]?.length) return arr[0][0];
    }
  }
  return null;
}
function firstPointCoord(
  fc: GeoJSON.FeatureCollection | null
): [number, number] | null {
  if (!fc) return null;
  for (const f of fc.features) {
    const g = (f as any).geometry as GeoJSON.Geometry | undefined;
    if (!g) continue;
    if (g.type === "Point") {
      return g.coordinates as [number, number];
    }
  }
  return null;
}
function pickInitialCoord(
  routeFC: GeoJSON.FeatureCollection | null,
  stationsFC: GeoJSON.FeatureCollection | null
): [number, number] {
  return firstLineCoord(routeFC) ?? firstPointCoord(stationsFC) ?? SCL_CENTER;
}

function disableMapInteractions(map: mapboxgl.Map) {
  try {
    map.boxZoom.disable();
    map.dragPan.disable();
    map.dragRotate.disable();
    map.keyboard.disable();
    map.doubleClickZoom.disable();
    map.scrollZoom.disable();
    map.touchZoomRotate.disable();
  } catch {}
}
function enableMapInteractions(map: mapboxgl.Map) {
  try {
    map.boxZoom.enable();
    map.dragPan.enable();
    map.dragRotate.enable();
    map.keyboard.enable();
    map.doubleClickZoom.enable();
    map.scrollZoom.enable();
    map.touchZoomRotate.enable();
  } catch {}
}

/** Espera a que el mapa haya dibujado al menos un frame en su estado actual */
function waitNextRender(map: mapboxgl.Map): Promise<void> {
  return new Promise((resolve) => {
    const onRender = () => {
      map.off("render", onRender);
      // Un micro-respiro evita tearing en algunos drivers
      requestAnimationFrame(() => resolve());
    };
    map.on("render", onRender);
    // Si ya ha renderizado, nos aseguramos de resolver igualmente
    if ((map as any)._frame) {
      // fallback defensivo
    }
  });
}

/** ===== Esperar fuentes/capas ===== */
async function waitForSources(
  map: mapboxgl.Map,
  sourceIds: string[],
  timeoutMs = 5000
): Promise<void> {
  const start = performance.now();

  if (!map.isStyleLoaded()) {
    await new Promise<void>((resolve) => {
      const onIdle = () => {
        map.off("idle", onIdle);
        resolve();
      };
      map.on("idle", onIdle);
    });
  }

  function sourcesReady(): boolean {
    try {
      return sourceIds.every((id) => {
        return typeof (map as any).isSourceLoaded === "function"
          ? (map as any).isSourceLoaded(id)
          : !!map.getSource(id);
      });
    } catch {
      return false;
    }
  }

  if (sourcesReady()) return;

  await new Promise<void>((resolve) => {
    const onSourceData = () => {
      if (sourcesReady()) {
        map.off("sourcedata", onSourceData);
        resolve();
      }
    };
    map.on("sourcedata", onSourceData);

    const tick = () => {
      if (sourcesReady()) {
        map.off("sourcedata", onSourceData);
        resolve();
        return;
      }
      if (performance.now() - start > timeoutMs) {
        map.off("sourcedata", onSourceData);
        resolve(); // evita deadlock
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/** ===== Utilidades Cruise ===== */
function haversineMeters(a: [number, number], b: [number, number]): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * R * Math.asin(Math.sqrt(h));
}
function sampleLineByStep(
  coords: [number, number][],
  stepMeters: number
): [number, number][] {
  if (coords.length < 2) return coords.slice();
  const out: [number, number][] = [];
  let carry = 0;
  out.push(coords[0]);
  for (let i = 1; i < coords.length; i++) {
    const A = coords[i - 1],
      B = coords[i];
    const segLen = haversineMeters(A, B);
    let distFromA = stepMeters - carry;
    while (distFromA <= segLen) {
      const t = distFromA / segLen;
      const lng = A[0] + (B[0] - A[0]) * t;
      const lat = A[1] + (B[1] - A[1]) * t;
      out.push([lng, lat]);
      distFromA += stepMeters;
    }
    carry = segLen - (distFromA - stepMeters);
    if (carry < 1e-6) carry = 0;
  }
  const last = coords[coords.length - 1];
  const lastOut = out[out.length - 1];
  if (!lastOut || lastOut[0] !== last[0] || lastOut[1] !== last[1])
    out.push(last);
  return out;
}
function collectLineCoords(fc: FC): [number, number][] {
  const out: [number, number][] = [];
  for (const f of fc.features) {
    const g = (f as any).geometry as GeoJSON.Geometry | undefined;
    if (!g) continue;
    switch (g.type) {
      case "LineString":
        out.push(...(g.coordinates as [number, number][]));
        break;
      case "MultiLineString":
        for (const line of g.coordinates as [number, number][][])
          out.push(...line);
        break;
    }
  }
  return out;
}
function bearingTo(a: [number, number], b: [number, number]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const dLng = toRad(b[0] - a[0]);
  const y = Math.sin(dLng) * Math.cos(toRad(b[1]));
  const x =
    Math.cos(toRad(a[1])) * Math.sin(toRad(b[1])) -
    Math.sin(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}
function normalizeBearing(b: number) {
  return b > 180 ? b - 360 : b;
}
